import React, { useEffect, useMemo, useState } from "react";
import { useTournament } from "@/lib/TournamentContext";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Target, Flame, Award, Zap, Check, X, Calendar, ArrowUp, ArrowDown, Minus, Users, Sparkles, Compass, Search, UserPlus, Globe, Lock, Clock, AlertTriangle } from "lucide-react";
import TeamBadge from "@/components/app/TeamBadge";
import { computeUnderdog } from "@/lib/underdog";
import { GroupRankShareCard } from "@/components/app/GroupRankShareCard";
import Walkthrough from "@/components/app/Walkthrough";
import ThemeToggle from "@/components/app/ThemeToggle";
import RollOfHonour from "@/pages/RollOfHonour";
import RivalryBoard from "@/components/app/RivalryBoard";

const MovementBadge = ({ movement, hasHistory }) => {
  if (!hasHistory) {
    return <span className="text-[10px] uppercase tracking-widest text-zinc-500 border border-zinc-800 px-1.5 py-0.5" data-testid="movement-new">New</span>;
  }
  if (movement > 0) {
    return (
      <span className="flex items-center gap-1 text-green-400 font-display text-sm" data-testid="movement-up">
        <ArrowUp size={14} /> {movement}
      </span>
    );
  }
  if (movement < 0) {
    return (
      <span className="flex items-center gap-1 text-red-400 font-display text-sm" data-testid="movement-down">
        <ArrowDown size={14} /> {Math.abs(movement)}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-zinc-500 font-display text-sm" data-testid="movement-none">
      <Minus size={14} />
    </span>
  );
};

const StatTile = ({ label, value, icon: Icon, testid, accent }) => (
  <div className={`surface-card p-4 md:p-5 ${accent ? "accent-bar" : ""}`} data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <span className="label-tag">{label}</span>
      <Icon size={16} className="text-zinc-500" />
    </div>
    <div className="font-display text-3xl md:text-4xl">{value}</div>
  </div>
);

const EntryChip = ({ label, entered, to, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`flex items-center justify-between px-3 py-2 border text-xs uppercase tracking-widest transition-colors ${
      entered ? "border-green-500/40 text-green-400 hover:bg-green-500/10" : "border-primary-accent/60 text-primary-accent hover:bg-primary-accent/10"
    }`}
  >
    <span>{label}</span>
    {entered ? <Check size={12}/> : <X size={12}/>}
  </Link>
);

const StatusChip = ({ label, done, to, testid, note, warn }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`flex items-center justify-between gap-1 px-3 py-2 border text-xs uppercase tracking-widest transition-colors ${
      warn ? "border-amber-500/60 text-amber-300 hover:bg-amber-500/10"
      : done ? "border-green-500/40 text-green-400 hover:bg-green-500/10"
      : "border-primary-accent/60 text-primary-accent hover:bg-primary-accent/10"
    }`}
  >
    <span className="truncate">{label}{note ? ` ${note}` : ""}</span>
    {warn ? <AlertTriangle size={12} className="flex-shrink-0"/> : done ? <Check size={12} className="flex-shrink-0"/> : <X size={12} className="flex-shrink-0"/>}
  </Link>
);

export default function Dashboard() {
  const { active } = useTournament();
  const { user } = useAuth();
  const [stats, setStats] = useState({ total_points: 0, predictions: 0, correct_results: 0, exact_scores: 0, best_streak: 0, current_streak: 0 });
  const [matches, setMatches] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [teams, setTeams] = useState({});
  const [myPredictions, setMyPredictions] = useState([]);
  const [mySurvivor, setMySurvivor] = useState([]);
  const [myBracket, setMyBracket] = useState(null);
  const [bracketRecheck, setBracketRecheck] = useState(false);
  const [bracketRecheckLabel, setBracketRecheckLabel] = useState("");
  const [myFantasy, setMyFantasy] = useState(null);
  const [bonusAnswered, setBonusAnswered] = useState(0);
  const [myGroups, setMyGroups] = useState([]);
  const [upset, setUpset] = useState(null);
  const [profile, setProfile] = useState(null);
  const [discover, setDiscover] = useState([]);
  const [discoverSearch, setDiscoverSearch] = useState("");
  const [joiningId, setJoiningId] = useState(null);
  const [requestingId, setRequestingId] = useState(null);
  const [pendingReqs, setPendingReqs] = useState({ total: 0, by_group: {} });
  const [livePoints, setLivePoints] = useState({});
  const [liveRank, setLiveRank] = useState(null); // { settled_rank, rank } for current user while live
  const [showTour, setShowTour] = useState(false);

  const tourKey = user?.user_id ? `jinx_tour_v1_${user.user_id}` : null;
  const closeTour = () => {
    setShowTour(false);
    if (tourKey) { try { localStorage.setItem(tourKey, "1"); } catch { /* ignore */ } }
  };

  // Auto-open the walkthrough on a user's first visit
  useEffect(() => {
    if (!tourKey) return;
    let t;
    try {
      if (!localStorage.getItem(tourKey)) t = setTimeout(() => setShowTour(true), 400);
    } catch { /* ignore */ }
    return () => { if (t) clearTimeout(t); };
  }, [tourKey]);

  useEffect(() => {
    if (!active || active.status === "archived") return;
    const tid = active.tournament_id;
    // Fetch each slice independently so the dashboard paints progressively — fast
    // sections (stats, matches, leaderboard) no longer wait on the single slowest
    // call (e.g. discover / upset) the way a blocking Promise.all forced them to.
    const g = (path, opts) => api.get(path, opts);
    g(`/tournaments/${tid}/my-stats`).then((r) => setStats(r.data)).catch(() => {});
    g(`/tournaments/${tid}/matches`).then((r) => setMatches(r.data)).catch(() => {});
    g(`/tournaments/${tid}/leaderboard`).then((r) => setLeaderboard(r.data.slice(0, 8))).catch(() => {});
    g(`/tournaments/${tid}/teams`).then((r) => setTeams(Object.fromEntries(r.data.map((t) => [t.team_id, t])))).catch(() => {});
    g(`/tournaments/${tid}/my-predictions`).then((r) => setMyPredictions(r.data)).catch(() => {});
    g(`/tournaments/${tid}/survivor/me`).then((r) => setMySurvivor(r.data.picks || [])).catch(() => {});
    g(`/tournaments/${tid}/bracket/me`).then((r) => setMyBracket(r.data)).catch(() => {});
    g(`/tournaments/${tid}/fantasy/me`).then((r) => setMyFantasy(r.data)).catch(() => {});
    g(`/tournaments/${tid}/my-groups`).then((r) => setMyGroups(r.data)).catch(() => {});
    g(`/tournaments/${tid}/upset-of-the-week`).then((r) => setUpset(r.data)).catch(() => {});
    g(`/profile/me`, { params: { tournament_id: tid } }).then((r) => setProfile(r.data)).catch(() => {});
    g(`/tournaments/${tid}/groups/discover`).then((r) => setDiscover(r.data)).catch(() => {});
    g(`/tournaments/${tid}/groups/pending-requests-count`).then((r) => setPendingReqs(r.data))
      .catch((err) => console.warn("pending-requests-count failed:", err?.message || err));
    // Proactively nudge users with an outdated submitted bracket
    g(`/tournaments/${tid}/bracket/recheck-status`)
      .then((r) => { setBracketRecheck(!!r.data?.needs_recheck); setBracketRecheckLabel(r.data?.changed || ""); })
      .catch(() => {});
    // Bonus answered count (single batched call — no longer one request per question)
    g(`/tournaments/${tid}/my-bonus-answers`)
      .then((r) => setBonusAnswered(r.data?.answered_count || 0))
      .catch(() => {});
  }, [active]);

  // Keep live scores fresh: re-fetch matches + live provisional points every 30s
  // so the "Live Now" cards reflect the latest polled scoreline and standing points.
  useEffect(() => {
    if (!active || active.status === "archived") return;
    const tid = active.tournament_id;
    const tick = () => {
      api.get(`/tournaments/${tid}/matches`).then((r) => setMatches(r.data)).catch(() => {});
      api.get(`/tournaments/${tid}/live-points`).then((r) => setLivePoints(r.data || {})).catch(() => {});
      api.get(`/tournaments/${tid}/leaderboard/provisional`).then((r) => {
        if (!r.data?.has_live) { setLiveRank(null); return; }
        const mine = (r.data.rows || []).find((row) => row.user_id === user?.user_id);
        setLiveRank(mine ? { settled_rank: mine.settled_rank, rank: mine.rank } : null);
      }).catch(() => {});
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [active]);

  // Live state — only admin-enabled (go-live) group matchdays + populated knockouts,
  // and only buckets that aren't finished yet. Drives the dashboard status + Next Up.
  const live = useMemo(() => {
    const now = new Date();
    const liveMDs = active?.config?.live_matchdays || [1];
    const isFinal = (m) => {
      const h = teams[m.home_team_id], a = teams[m.away_team_id];
      return h && h.code !== "TBD" && a && a.code !== "TBD";
    };
    const buckets = new Map();
    matches.forEach((m) => {
      const isGroup = m.stage === "group" && m.matchday != null;
      const key = isGroup ? `md:${m.matchday}` : `stage:${m.stage}`;
      if (!buckets.has(key)) buckets.set(key, {
        key, isGroup, md: isGroup ? m.matchday : null,
        label: isGroup ? `Matchday ${m.matchday}` : (m.stage || "").toUpperCase(), matches: [],
      });
      buckets.get(key).matches.push(m);
    });
    const activeBuckets = [...buckets.values()].filter((b) => {
      if (!b.matches.some((m) => new Date(m.kickoff) > now)) return false; // finished → hide
      if (b.isGroup) return liveMDs.includes(b.md);
      return b.matches.every((m) => isFinal(m)); // knockout populated (teams decided)
    });
    const predIds = new Set(myPredictions.map((p) => p.match_id));
    const groupBuckets = activeBuckets.filter((b) => b.isGroup);
    const koBuckets = activeBuckets.filter((b) => !b.isGroup);
    const groupMatches = groupBuckets.flatMap((b) => b.matches);
    const koMatches = koBuckets.flatMap((b) => b.matches);
    const sorted = [...activeBuckets].sort((a, b) =>
      Math.min(...a.matches.map((m) => +new Date(m.kickoff))) - Math.min(...b.matches.map((m) => +new Date(m.kickoff)))
    );
    const next = sorted[0];
    const nextMatches = next
      ? next.matches.filter((m) => new Date(m.kickoff) > now).sort((a, b) => new Date(a.kickoff) - new Date(b.kickoff))
      : [];
    const survDoneMDs = new Set();
    const survKO = new Set();
    mySurvivor.forEach((sp) => {
      const m = matches.find((x) => x.match_id === sp.match_id);
      if (!m) return;
      if (m.stage === "group" && m.matchday != null) survDoneMDs.add(m.matchday);
      else if (m.stage !== "group") survKO.add(m.stage);
    });
    return {
      groupTotal: groupMatches.length,
      groupDone: groupMatches.filter((m) => predIds.has(m.match_id)).length,
      koTotal: koMatches.length,
      koDone: koMatches.filter((m) => predIds.has(m.match_id)).length,
      koActive: koBuckets.length > 0,
      nextMatches, nextLabel: next?.label || null,
      activeGroupMDs: groupBuckets.length,
      survGroupDone: groupBuckets.filter((b) => survDoneMDs.has(b.md)).length,
      survKODone: survKO.size > 0,
      bracketComplete: !!myBracket?.champion,
    };
  }, [matches, teams, myPredictions, mySurvivor, myBracket, active]);

  const entries = useMemo(() => ({
    fantasy: (myFantasy?.player_ids || []).length === 11,
    bonus: bonusAnswered > 0,
  }), [myFantasy, bonusAnswered]);

  // In-play games: locked (kicked off) but not yet settled — shown until they settle.
  // Driven by lock time (not just provider "live" status) so cards appear as soon as
  // a match locks, even if the live-status sync lags.
  const inPlayMatches = useMemo(() => {
    const now = Date.now();
    const cutoff = now - 24 * 60 * 60 * 1000;
    return matches
      .filter((m) => {
        if (m.settled) return false;
        if (m.status === "live") return true; // provider/admin marked live — show regardless of clock
        const k = new Date(m.kickoff).getTime();
        return k <= now && k >= cutoff;
      })
      .sort((a, b) => new Date(b.kickoff) - new Date(a.kickoff));
  }, [matches]);

  // First-run setup checklist (auto-hides once everything is done)
  const setup = useMemo(() => {
    const joinedGroup = myGroups.length > 0;
    const displayNameSet = !!(profile?.has_profile && profile?.display_name);
    const madePrediction = myPredictions.length > 0;
    const items = [
      { key: "name", label: "Set a display name", done: displayNameSet, to: "/profile" },
      { key: "group", label: "Join a group", done: joinedGroup, to: "/profile" },
      { key: "predict", label: "Make your first prediction", done: madePrediction, to: "/predict" },
    ];
    if (active?.format === "league" && (active?.config?.club_groups_enabled ?? true)) {
      items.splice(1, 0, { key: "club", label: "Pick your favourite club", done: !!profile?.favourite_team, to: "/profile" });
    }
    return { items, allDone: items.every((i) => i.done) };
  }, [myGroups, profile, myPredictions, active]);

  // "Upset Specials" — top 3 open upcoming games with the biggest underdog bonus on offer.
  const upsetSpecials = useMemo(() => {
    if (!active) return [];
    const liveMds = active.config?.live_matchdays || [1];
    const homeAdv = active.config?.elo_home_advantage ?? 50;
    const now = Date.now();
    const out = [];
    for (const m of matches) {
      if (m.status === "completed") continue;
      if (m.kickoff && new Date(m.kickoff).getTime() <= now) continue;
      if (m.stage === "group" && m.matchday != null && !liveMds.includes(m.matchday)) continue;
      const h = teams[m.home_team_id];
      const a = teams[m.away_team_id];
      if (!h || !a) continue;
      const ud = computeUnderdog(h, a, homeAdv);
      if (!ud || ud.finalBonus <= 0) continue;
      const underdog = ud.underdogSide === "home" ? h : a;
      const favourite = ud.underdogSide === "home" ? a : h;
      out.push({ match: m, info: ud, underdog, favourite });
    }
    out.sort((x, y) => y.info.finalBonus - x.info.finalBonus);
    return out.slice(0, 3);
  }, [matches, teams, active]);

  if (!active) return null;

  if (active.status === "archived") return <RollOfHonour />;

  const tid = active.tournament_id;
  const filteredDiscover = discover.filter((g) =>
    g.name.toLowerCase().includes(discoverSearch.trim().toLowerCase())
  );

  const joinGroup = async (gid) => {
    setJoiningId(gid);
    try {
      await api.post(`/groups/${gid}/join`);
      toast.success("Joined group");
      const [dc, mg] = await Promise.all([
        api.get(`/tournaments/${tid}/groups/discover`),
        api.get(`/tournaments/${tid}/my-groups`),
      ]);
      setDiscover(dc.data);
      setMyGroups(mg.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't join group");
    } finally { setJoiningId(null); }
  };

  const requestJoin = async (gid) => {
    setRequestingId(gid);
    try {
      await api.post(`/groups/${gid}/request-join`);
      toast.success("Request sent — a group admin will review it");
      const { data } = await api.get(`/tournaments/${tid}/groups/discover`);
      setDiscover(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send request");
    } finally { setRequestingId(null); }
  };


  return (
    <div className="px-3 md:px-8 py-6 md:py-8 grid-bg min-h-[calc(100vh-65px)]">
      <Walkthrough open={showTour} onClose={closeTour} competitions={active.competitions || {}} />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 md:mb-10 gap-4">
        <div>
          <div className="label-tag mb-2 text-primary-accent">Welcome, {user?.name}</div>
          <h1 className="font-display text-3xl md:text-6xl uppercase leading-none">{active.name}</h1>
          <div className="text-zinc-500 mt-2 text-xs md:text-sm uppercase tracking-widest hidden md:block">
            {Object.entries(active.competitions || {}).filter(([_, v]) => v).map(([k]) => k.replace("_", " ")).join(" · ")}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <button data-testid="app-tour-btn" onClick={() => setShowTour(true)}
            className="surface-card px-4 py-3 font-display tracking-widest text-sm uppercase text-primary-accent hover:border-primary-accent transition-colors flex items-center gap-2">
            <Compass size={16} /> App Tour
          </button>
          <Link to="/predict" data-testid="cta-predict" className="bg-primary-accent px-5 py-3 font-display tracking-widest text-base md:text-lg uppercase hover:bg-[var(--primary-hover)] transition-colors text-center">
            Make Predictions →
          </Link>
        </div>
      </div>

      {inPlayMatches.length > 0 && (
        <div className="mb-6 md:mb-8" data-testid="live-now-section">
          <div className="flex items-center gap-2 mb-3">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
            </span>
            <div className="label-tag text-green-400">Live Now</div>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">
              {inPlayMatches.length} game{inPlayMatches.length > 1 ? "s" : ""} in play
            </span>
            {liveRank && liveRank.rank != null && (
              <span
                data-testid="live-rank-chip"
                className={`ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-display px-2 py-1 rounded ${
                  liveRank.settled_rank != null && liveRank.rank < liveRank.settled_rank
                    ? "text-green-400 bg-green-500/10"
                    : liveRank.settled_rank != null && liveRank.rank > liveRank.settled_rank
                    ? "text-primary-accent bg-primary-accent/10"
                    : "text-zinc-400 bg-white/5"
                }`}
              >
                {liveRank.settled_rank != null && liveRank.rank < liveRank.settled_rank
                  ? `▲ Moving up to #${liveRank.rank}`
                  : liveRank.settled_rank != null && liveRank.rank > liveRank.settled_rank
                  ? `▼ Slipping to #${liveRank.rank}`
                  : `Holding #${liveRank.rank}`}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {inPlayMatches.map((m) => {
              const lp = livePoints[m.match_id];
              const isLive = m.status === "live";
              const isFT = m.status === "completed";
              return (
                <div
                  key={m.match_id}
                  data-testid={`live-game-${m.match_id}`}
                  className={`surface-card p-4 border ${isLive ? "border-green-500/40" : isFT ? "border-amber-500/40" : "border-white/10"}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500" data-testid={`live-kickoff-${m.match_id}`}>
                      {m.stage === "group" && m.matchday != null ? `Matchday ${m.matchday}` : (m.stage || "").toUpperCase()}
                      {m.kickoff && (
                        <span className="text-zinc-600"> · {new Date(m.kickoff).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                      )}
                    </span>
                    {isLive ? (
                      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-green-400 font-display" data-testid={`live-badge-${m.match_id}`}>
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                        Live
                      </span>
                    ) : isFT ? (
                      <span className="text-[10px] uppercase tracking-widest text-amber-400 font-display" data-testid={`live-badge-${m.match_id}`}>FT · settling</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-display" data-testid={`live-badge-${m.match_id}`}>Locked</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0"><TeamBadge team={teams[m.home_team_id]} size="md" /></div>
                    <div className="font-display text-2xl md:text-3xl tabular-nums px-2 whitespace-nowrap" data-testid={`live-score-${m.match_id}`}>
                      {m.home_score ?? 0}<span className="text-zinc-600 mx-1">–</span>{m.away_score ?? 0}
                    </div>
                    <div className="flex-1 min-w-0 flex justify-end"><TeamBadge team={teams[m.away_team_id]} size="md" /></div>
                  </div>
                  {lp ? (
                    <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between" data-testid={`live-points-${m.match_id}`}>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                        Your pick {lp.home_pred}–{lp.away_pred}
                        {lp.is_captain && <span className="text-yellow-400 ml-1">★</span>}
                        {lp.power_play && <span className="text-primary-accent ml-1">3x</span>}
                      </span>
                      {lp.points != null ? (
                        <span className={`font-display text-lg tabular-nums ${lp.points > 0 ? "text-green-400" : "text-zinc-500"}`}>
                          {lp.points > 0 ? `+${lp.points}` : "0"} <span className="text-[10px] uppercase tracking-widest text-zinc-500">pts as it stands</span>
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest text-zinc-500">awaiting score</span>
                      )}
                    </div>
                  ) : (
                    <div className="mt-3 pt-3 border-t border-white/5 text-[10px] uppercase tracking-widest text-zinc-600" data-testid={`live-nopick-${m.match_id}`}>
                      No prediction on this game
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!setup.allDone && (
        <div className="surface-card p-4 md:p-5 mb-6 border border-primary-accent/40" data-testid="setup-checklist">
          <div className="flex items-center gap-2 mb-3">
            <Compass size={18} className="text-primary-accent" />
            <div className="label-tag text-primary-accent">Finish Setup</div>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500" data-testid="setup-progress">
              {setup.items.filter((i) => i.done).length}/{setup.items.length} done
            </span>
          </div>
          <p className="text-xs text-zinc-400 mb-4">A couple of quick steps to get the most out of Jinxing Sports.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {setup.items.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                data-testid={`setup-item-${item.key}`}
                className={`flex items-center justify-between gap-2 px-3 py-2.5 border text-xs uppercase tracking-widest transition-colors ${
                  item.done ? "border-green-500/40 text-green-400 hover:bg-green-500/10" : "border-primary-accent/60 text-primary-accent hover:bg-primary-accent/10"
                }`}
              >
                <span className="truncate">{item.label}</span>
                {item.done ? <Check size={14} className="flex-shrink-0" /> : <X size={14} className="flex-shrink-0" />}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-4 mb-6 md:mb-10">
        <StatTile testid="stat-points" label="Points" value={stats.total_points} icon={Trophy} accent />
        <StatTile testid="stat-predictions" label="Predictions" value={stats.predictions} icon={Target} />
        <StatTile testid="stat-correct" label="Correct" value={stats.correct_results} icon={Award} />
        <StatTile testid="stat-exact" label="Exact" value={stats.exact_scores} icon={Zap} />
        <StatTile testid="stat-streak" label="Best Streak" value={stats.best_streak} icon={Flame} />
        <StatTile testid="stat-current-streak" label="Current" value={stats.current_streak} icon={Flame} />
      </div>

      {/* UPSET OF THE WEEK */}
      {upset?.upset && (
        <div className="surface-card p-4 md:p-5 mb-6 border border-primary-accent/40 bg-primary-accent/[0.06]" data-testid="upset-of-week">
          <div className="flex items-center gap-2 mb-3">
            <Flame size={18} className="text-primary-accent" />
            <div className="label-tag text-primary-accent">Upset of the Week</div>
          </div>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="font-display text-lg md:text-xl uppercase">
                <span className="text-primary-accent">{upset.upset.user_name}</span> called the upset
              </div>
              <div className="text-sm text-zinc-300 flex items-center gap-2 mt-1.5 flex-wrap">
                <TeamBadge team={upset.upset.underdog} size="sm" />
                <span className="text-zinc-500 lowercase tracking-normal">beat</span>
                <TeamBadge team={upset.upset.favourite} size="sm" />
                <span className="text-zinc-500">· final {upset.upset.final_score}</span>
                <span className="text-zinc-600">· predicted {upset.upset.predicted}</span>
              </div>
            </div>
            <div className="text-left md:text-right flex-shrink-0">
              <div className="font-display text-3xl text-primary-accent leading-none">+{upset.upset.bonus}</div>
              <div className="label-tag mt-1">Underdog Bonus</div>
              {upset.week_count > 1 && (
                <div className="text-[11px] text-zinc-500 mt-1">+{upset.week_count - 1} more upset{upset.week_count - 1 > 1 ? "s" : ""} this week</div>
              )}
            </div>
          </div>
        </div>
      )}

      {upsetSpecials.length > 0 && (
        <div className="surface-card p-4 md:p-5 mb-6" data-testid="upset-specials">
          <div className="flex items-center gap-2 mb-3">
            <Flame size={16} className="text-primary-accent" />
            <h2 className="font-display text-xl uppercase tracking-tight">Upset Specials</h2>
            <span className="label-tag ml-1">Biggest bonuses on offer</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {upsetSpecials.map(({ match: m, info, underdog, favourite }) => (
              <Link
                key={m.match_id}
                to="/predict"
                data-testid={`upset-special-${m.match_id}`}
                className="border border-primary-accent/30 bg-primary-accent/[0.05] p-3 hover:border-primary-accent/70 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">{info.label}</span>
                  <span className="font-display text-2xl text-primary-accent leading-none">+{info.finalBonus}</span>
                </div>
                <div className="flex items-center gap-1.5 mb-1">
                  {underdog.crest && <img src={underdog.crest} alt="" className="w-5 h-5 object-contain" />}
                  <span className="font-display text-sm tracking-wider truncate">{underdog.code || underdog.name}</span>
                  <span className="text-[9px] uppercase tracking-widest text-primary-accent border border-primary-accent/40 px-1">Underdog</span>
                </div>
                <div className="text-[11px] text-zinc-500 truncate">
                  to beat {favourite.code || favourite.name}
                  {info.system === "elo" && <span className="text-zinc-600"> · {info.underdogPct}% win prob</span>}
                </div>
              </Link>
            ))}
          </div>
          <div className="text-[11px] text-zinc-500 mt-2">Nail the underdog and bank the bonus on top of your points.</div>
        </div>
      )}

      {/* Matchday section */}
      {live.nextMatches.length > 0 && (
        <div className="surface-card p-4 md:p-6 mb-6 accent-bar" data-testid="matchday-section">
          <div className="flex items-start md:items-center justify-between flex-col md:flex-row gap-3 mb-4">
            <div className="flex items-center gap-3">
              <Calendar size={18} className="text-primary-accent"/>
              <div>
                <div className="label-tag">Next Up</div>
                <h2 className="font-display text-xl md:text-2xl uppercase">{live.nextLabel}</h2>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 w-full md:w-auto md:justify-end">
              {active.competitions?.main_league && live.groupTotal > 0 && <StatusChip testid="entry-predict-group" label="Predict · Group" note={`${live.groupDone}/${live.groupTotal}`} done={live.groupDone === live.groupTotal} to="/predict" />}
              {active.competitions?.main_league && live.koTotal > 0 && <StatusChip testid="entry-predict-ko" label="Predict · KO" note={`${live.koDone}/${live.koTotal}`} done={live.koDone === live.koTotal} to="/predict" />}
              {active.competitions?.survivor && live.activeGroupMDs > 0 && <StatusChip testid="entry-survivor-group" label="Surv · Group" note={`${live.survGroupDone}/${live.activeGroupMDs}`} done={live.survGroupDone === live.activeGroupMDs} to="/survivor" />}
              {active.competitions?.survivor && live.koActive && <StatusChip testid="entry-survivor-ko" label="Surv · KO" done={live.survKODone} to="/survivor" />}
              {active.competitions?.bracket && <StatusChip testid="entry-bracket" label={bracketRecheck ? `Bracket · ${bracketRecheckLabel || "Review"}` : "Bracket"} done={live.bracketComplete} warn={bracketRecheck} to="/bracket" />}
              {active.competitions?.fantasy && <EntryChip testid="entry-fantasy" label="Fantasy" entered={entries.fantasy} to="/fantasy" />}
              {active.competitions?.bonus_questions && <EntryChip testid="entry-bonus" label="Bonus" entered={entries.bonus} to="/bonus" />}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {live.nextMatches.slice(0, 6).map((m) => {
              const h = teams[m.home_team_id];
              const a = teams[m.away_team_id];
              const myP = myPredictions.find((p) => p.match_id === m.match_id);
              return (
                <Link key={m.match_id} to="/predict" className="border border-zinc-800 p-3 hover:border-white/30 transition-colors" data-testid={`md-match-${m.match_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="label-tag">{m.group ? `Group ${m.group}` : m.stage.toUpperCase()}</span>
                    <span className="text-xs text-zinc-500">
                      {new Date(m.kickoff).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <TeamBadge team={h} size="sm" hideCodeOnMobile />
                      {myP && <span className="font-display text-lg tabular-nums">{myP.home_pred}</span>}
                    </div>
                    <div className="flex items-center justify-between">
                      <TeamBadge team={a} size="sm" hideCodeOnMobile />
                      {myP && <span className="font-display text-lg tabular-nums">{myP.away_pred}</span>}
                    </div>
                  </div>
                  {!myP && <div className="text-xs uppercase tracking-widest text-primary-accent mt-2">No prediction →</div>}
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* MY GROUPS — user-specific hero panel */}
        <div className="surface-card p-4 md:p-6 lg:col-span-2 accent-bar" data-testid="my-groups-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users size={18} className="text-primary-accent" />
              <h2 className="font-display text-xl md:text-2xl uppercase tracking-tight">My Groups</h2>
            </div>
            <Link to="/profile" className="relative inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-primary-accent" data-testid="manage-groups-link">
              Manage →
              {pendingReqs.total > 0 && (
                <span data-testid="manage-pending-badge" className="min-w-[18px] h-[18px] px-1 bg-primary-accent text-white text-[10px] font-display flex items-center justify-center rounded-full" title={`${pendingReqs.total} pending join request${pendingReqs.total === 1 ? "" : "s"}`}>
                  {pendingReqs.total > 9 ? "9+" : pendingReqs.total}
                </span>
              )}
            </Link>
          </div>

          {myGroups.length === 0 ? (
            <div className="text-center py-8" data-testid="no-groups-cta">
              <Sparkles size={28} className="text-primary-accent mx-auto mb-3" />
              <div className="font-display text-lg uppercase mb-1">You&apos;re not in a group yet</div>
              <p className="text-sm text-zinc-400 mb-4 max-w-sm mx-auto">Join or create a group to climb the rankings against your friends and track your live standing.</p>
              <Link to="/profile" data-testid="join-group-btn" className="inline-block bg-primary-accent text-white px-5 py-2.5 font-display tracking-widest text-sm uppercase hover:bg-[var(--primary-hover)] transition-colors">
                Join a Group
              </Link>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="hidden sm:flex items-center justify-between px-2 pb-2 text-[10px] uppercase tracking-widest text-zinc-600">
                <span>Group</span>
                <div className="flex items-center gap-8">
                  <span className="w-16 text-right">Your pts</span>
                  <span className="w-20 text-right">Movement</span>
                  <span className="w-12 text-right">Rank</span>
                </div>
              </div>
              {myGroups.map((g) => (
                <Link
                  key={g.group_id}
                  to={`/groups/${g.group_id}`}
                  data-testid={`my-group-${g.group_id}`}
                  className="flex items-center justify-between border border-white/5 hover:border-white/20 hover:bg-white/[0.03] px-3 py-3 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ background: g.color || "var(--primary)" }}
                    />
                    <div className="min-w-0">
                      <div className="font-display uppercase tracking-wide truncate text-sm md:text-base flex items-center gap-1.5">
                        <span className="truncate">{g.name}</span>
                        {pendingReqs.by_group[g.group_id] > 0 && (
                          <span data-testid={`group-pending-badge-${g.group_id}`} className="flex-shrink-0 min-w-[18px] h-[18px] px-1 bg-primary-accent text-white text-[10px] font-display flex items-center justify-center rounded-full" title={`${pendingReqs.by_group[g.group_id]} pending join request${pendingReqs.by_group[g.group_id] === 1 ? "" : "s"}`}>
                            {pendingReqs.by_group[g.group_id]}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500">{g.member_count} {g.member_count === 1 ? "member" : "members"}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 md:gap-8">
                    <span className="w-16 text-right font-display text-lg tabular-nums hidden sm:block">{g.points}</span>
                    <span className="w-20 flex justify-end"><MovementBadge movement={g.movement} hasHistory={g.has_history} /></span>
                    <span className={`w-12 text-right font-display text-2xl tabular-nums ${g.rank <= 3 ? "text-primary-accent" : ""}`}>#{g.rank}</span>
                    <GroupRankShareCard group={g} tournamentName={active.name} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* GLOBAL TOP PREDICTORS — secondary */}
        <div className="surface-card p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl md:text-2xl uppercase tracking-tight">Top Predictors</h2>
            <Link to="/leaderboard" className="text-xs uppercase tracking-widest text-primary-accent">All →</Link>
          </div>
          {leaderboard.length === 0 && <div className="text-zinc-500 text-sm">No leaderboard data yet.</div>}
          <div className="space-y-2">
            {leaderboard.map((row) => (
              <div key={row.user_id} className="flex items-center justify-between" data-testid={`lb-row-${row.user_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`w-6 text-right font-display flex-shrink-0 ${row.rank <= 3 ? "text-primary-accent" : "text-zinc-500"}`}>{row.rank}</span>
                  <span className="text-sm truncate">{row.name}</span>
                </div>
                <span className="font-display text-lg flex-shrink-0">{row.points}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* DISCOVER GROUPS — public groups to join */}
      <div className="surface-card p-4 md:p-6 mt-4 md:mt-6" data-testid="discover-groups-panel">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Globe size={18} className="text-primary-accent" />
            <h2 className="font-display text-xl md:text-2xl uppercase tracking-tight">Discover Groups</h2>
          </div>
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="discover-search"
              type="text" placeholder="Search groups…"
              value={discoverSearch} onChange={(e) => setDiscoverSearch(e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 pl-9 pr-3 py-2 text-sm focus:border-primary-accent focus:outline-none"
            />
          </div>
        </div>

        {discover.length === 0 ? (
          <div className="text-sm text-zinc-500 py-4" data-testid="discover-empty">
            No groups to join right now — create or request one from your <Link to="/profile" className="text-primary-accent">Profile</Link>.
          </div>
        ) : filteredDiscover.length === 0 ? (
          <div className="text-sm text-zinc-500 py-4">No groups match &ldquo;{discoverSearch}&rdquo;.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-80 overflow-y-auto pr-1">
            {filteredDiscover.map((g) => (
              <div
                key={g.group_id}
                data-testid={`discover-group-${g.group_id}`}
                className="flex items-center justify-between gap-3 border border-white/5 hover:border-white/20 px-3 py-3 transition-colors"
                style={{ borderLeftWidth: 4, borderLeftColor: g.color || "var(--primary)" }}
              >
                <Link to={`/groups/${g.group_id}`} className="min-w-0 flex-1">
                  <div className="font-display uppercase tracking-wide truncate text-sm flex items-center gap-1.5">
                    {g.visibility === "invite_only" && <Lock size={11} className="text-zinc-500 flex-shrink-0" />}
                    <span className="truncate">{g.name}</span>
                  </div>
                  <div className="text-xs text-zinc-500 flex items-center gap-1">
                    <Users size={11} /> {g.member_count} {g.member_count === 1 ? "member" : "members"}
                  </div>
                </Link>
                {g.visibility === "invite_only" ? (
                  g.request_status === "pending" ? (
                    <button
                      data-testid={`requested-${g.group_id}`}
                      disabled
                      className="flex items-center gap-1 border border-zinc-700 text-zinc-400 px-3 py-1.5 text-xs uppercase tracking-widest flex-shrink-0 cursor-default"
                    >
                      <Clock size={12} /> Requested
                    </button>
                  ) : (
                    <button
                      data-testid={`request-join-${g.group_id}`}
                      onClick={() => requestJoin(g.group_id)}
                      disabled={requestingId === g.group_id}
                      className="flex items-center gap-1 border border-primary-accent text-primary-accent px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-primary-accent hover:text-white transition-colors disabled:opacity-50 flex-shrink-0"
                    >
                      <Lock size={12} /> {requestingId === g.group_id ? "…" : "Request to Join"}
                    </button>
                  )
                ) : (
                  <button
                    data-testid={`join-group-${g.group_id}`}
                    onClick={() => joinGroup(g.group_id)}
                    disabled={joiningId === g.group_id}
                    className="flex items-center gap-1 bg-primary-accent text-white px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-[var(--primary-hover)] disabled:opacity-50 flex-shrink-0"
                  >
                    <UserPlus size={12} /> {joiningId === g.group_id ? "…" : "Join"}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <RivalryBoard tournamentId={tid} />
    </div>
  );
}
